/* -----------------------------------------------------------------------------
 * Checkbox
 * -------------------------------------------------------------------------- */
import { getLogger } from './utils/logger-context';
import { resolveComponentScope } from './utils/resolveComponentScope';

const COMPONENT_NAME = 'Checkbox';

export const init = (unresolvedScope) => {
    const logger = getLogger();
    const scope = resolveComponentScope(unresolvedScope);
    const defaultLoggerArgs = {
        componentName: COMPONENT_NAME,
        scope,
    };
    logger.debug(`Initialising ${COMPONENT_NAME} component.`, { scope });

    // Create custom checkbox
    const takoCheckboxes = scope.querySelectorAll(
        "input[type='checkbox'].tako-form-control:not([role='switch'])",
    );

    takoCheckboxes.forEach((checkbox) => {
        try {
            const checkboxParent = checkbox.parentElement;

            // Add class to parent label to be able to apply styles
            if (checkboxParent.tagName === 'LABEL') {
                if (checkbox.disabled) {
                    checkboxParent.classList.add(
                        'tako-checkbox__parent-label',
                        'tako-checkbox__parent-label-disabled',
                    );
                } else {
                    checkboxParent.classList.add('tako-checkbox__parent-label');
                }
            }

            // Wrap the checkbox and custom checkbox in a container for positioning
            const checkboxWrapper = document.createElement('div');
            checkboxWrapper.classList.add('tako-checkbox__wrapper');
            checkbox.parentNode.insertBefore(checkboxWrapper, checkbox);
            checkboxWrapper.appendChild(checkbox);

            // Add custom tako checkbox
            const customTakoCheckbox = document.createElement('span');
            customTakoCheckbox.classList.add(
                'tako-form-control',
                'tako-form-control--checkmark',
            );
            const checkIcon = `<span class="tako-icon tako-checkbox__check-icon"><i class="far fa-check"></i></span>`;
            const indeterminateIcon = `<span class="tako-icon tako-checkbox__indeterminate-icon"><i class="far fa-minus"></i></span>`;
            customTakoCheckbox.innerHTML = checkIcon + indeterminateIcon;

            checkbox.style.opacity = '0';
            checkbox.parentNode.insertBefore(
                customTakoCheckbox,
                checkbox.nextSibling,
            );

            // Toggle all checkboxes in checkbox groups when checking the parent checkbox
            const isMultiCheckboxParent =
                checkbox.hasAttribute('data-select-all');

            if (isMultiCheckboxParent) {
                toggleCheckboxGroup(checkbox);
            }
        } catch (error) {
            logger.error(error, {
                ...defaultLoggerArgs,
                element: checkbox,
            });
        }
    });

    // Toggle checkbox when hitting enter
    takoCheckboxes.forEach((el) => {
        el.addEventListener('keydown', (evt) => {
            const isTakoCheckboxInputChecked = evt.target.checked
                ? true
                : false;
            if (evt.key === 'Enter') {
                evt.preventDefault();
                evt.target.checked = !isTakoCheckboxInputChecked;
                evt.target.indeterminate = false;
            }
        });
    });
};

const toggleCheckboxGroup = (parentCheckbox) => {
    const logger = getLogger();

    const multiCheckboxTarget = parentCheckbox.getAttribute('data-select-all');
    const multiCheckboxGroup = document.querySelector(
        `#${multiCheckboxTarget}`,
    );

    if (!multiCheckboxGroup) {
        logger.warn(
            `Tako Checkbox: no checkbox group found with the id "${multiCheckboxTarget}".`,
        );
        return;
    }

    const multiCheckboxes = multiCheckboxGroup.querySelectorAll(
        "input[type='checkbox']",
    );

    // Toggle parent checkbox visibility
    const parentCheckboxWrapper =
        parentCheckbox.closest('.tako-checkbox__parent-label') ||
        parentCheckbox.closest('.tako-checkbox__wrapper');

    if (multiCheckboxes.length <= 1) {
        parentCheckboxWrapper.style.display = 'none';
    } else {
        parentCheckboxWrapper.style.display = '';
    }

    const getCheckboxGroupStatus = () => {
        let allChecked = true;
        let someChecked = false;

        for (const checkbox of multiCheckboxes) {
            if (checkbox.checked) {
                someChecked = true;
            } else {
                allChecked = false;
            }

            if (someChecked && !allChecked) {
                break; // Early exit
            }
        }

        return { allChecked, someChecked };
    };

    const toggleCheckboxes = () => {
        const { allChecked, someChecked } = getCheckboxGroupStatus();

        multiCheckboxes.forEach((multiCheckbox) => {
            // If some are checked but not all, set all to true; otherwise, toggle.
            multiCheckbox.checked =
                someChecked && !allChecked ? true : !multiCheckbox.checked;
        });
    };

    const updateParentCheckbox = () => {
        const { allChecked, someChecked } = getCheckboxGroupStatus();

        if (allChecked) {
            parentCheckbox.indeterminate = false;
            parentCheckbox.checked = true;
        } else if (someChecked) {
            parentCheckbox.indeterminate = true;
            parentCheckbox.checked = false;
        } else {
            parentCheckbox.indeterminate = false;
            parentCheckbox.checked = false;
        }
    };

    // Handle click on individual checkboxes with shift selection
    let lastChecked = null;
    const selectRangeOfCheckboxesWithShift = (evt, multiCheckbox) => {
        if (evt.shiftKey) {
            if (!lastChecked) {
                lastChecked = multiCheckbox;
                return;
            }

            const checkboxes = Array.from(multiCheckboxes);
            const start = checkboxes.indexOf(multiCheckbox);
            const end = checkboxes.indexOf(lastChecked);
            const [low, high] = [Math.min(start, end), Math.max(start, end)];

            for (let i = low; i <= high; i++) {
                checkboxes[i].checked = lastChecked.checked;
            }
        }

        lastChecked = multiCheckbox;
    };

    // Add event listeners to parent checkbox
    parentCheckbox.addEventListener('click', toggleCheckboxes);
    parentCheckbox.addEventListener('keydown', (evt) => {
        if (evt.key === 'Enter') {
            evt.preventDefault();
            toggleCheckboxes();
        }
    });

    // Add event listeners to checkboxes in the group
    multiCheckboxes.forEach((multiCheckbox) => {
        multiCheckbox.addEventListener('click', (evt) => {
            selectRangeOfCheckboxesWithShift(evt, multiCheckbox);
            updateParentCheckbox();
        });

        // Update status of parent checkbox when hitting enter
        multiCheckbox.addEventListener('keyup', (evt) => {
            if (evt.key === 'Enter') {
                evt.preventDefault();
                updateParentCheckbox();
            }
        });
    });

    updateParentCheckbox();
};
